/**
 * Firestore — admin_events collection
 *
 * Every mode (Ask, Draft, Analyse, Workflows) writes events here.
 * The admin dashboard reads from this collection for usage stats.
 */
import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit as fbLimit,
  onSnapshot,
  Timestamp,
  type Unsubscribe,
} from "firebase/firestore";
import app from "../client";

const db = getFirestore(app);
const EVENTS = "admin_events";

// ─── Event Types ─────────────────────────────────────────────────────────────

export type AdminEventType =
  | "ask_session_started"
  | "ask_message_sent"
  | "ask_message_received"
  | "draft_created"
  | "draft_chat_message"
  | "draft_modified"
  | "analysis_started"
  | "analysis_completed"
  | "rfp_project_created"
  | "rfp_step_completed"
  | "rfq_project_created"
  | "rfq_step_completed"
  | "supplier_eval_created"
  | "workflow_rfp_agent_call"
  | "workflow_rfq_agent_call"
  | "workflow_supplier_eval"
  | "workflow_draft_modify_call"
  | "analysis_agent_call"
  | "user_active";

export interface AdminEvent {
  id: string;
  type: AdminEventType;
  userId: string;
  timestamp: Timestamp;
  metadata: Record<string, unknown>;
}

/**
 * Canonical token-attribution contract for admin_event metadata.
 *
 * Every service that logs AI usage (Ask, Draft, Analyse, Workflows) writes these
 * keys into `metadata` so the admin cost pages can price each event by model.
 * Fields not present on an event are treated as "unattributed" (tokens still
 * counted, cost not priced) by the cost aggregator.
 *
 * @metadata:
 *   provider      string  — "anthropic" | "vertex" | "deepseek" | ...
 *   model         string  — model id (e.g. "claude-sonnet-4-5", "gemini-2.5-pro")
 *   mode          string  — product mode (e.g. "doc" | "deck" | "expert" …)
 *   feature       string  — workflow feature/step (e.g. "rfp", "rfq", "supplier-eval")
 *   input_tokens  number
 *   output_tokens number
 *   total_tokens  number
 *   cache_read_input_tokens    number?  (Claude cache)
 *   cache_creation_input_tokens number? (Claude cache)
 */
export interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
}

// ─── Write ───────────────────────────────────────────────────────────────────

export async function logAdminEvent(
  eventType: AdminEventType,
  userId: string,
  metadata: Record<string, unknown> = {}
): Promise<void> {
  try {
    await addDoc(collection(db, EVENTS), {
      type: eventType,
      userId,
      timestamp: Timestamp.now(),
      metadata,
    });
  } catch (err) {
    // Best-effort — never block user-facing flows
    console.warn("[admin-events] failed to log:", eventType, err);
  }
}

// ─── Convenience wrappers ────────────────────────────────────────────────────

export function logAskSessionStarted(userId: string, sessionId: string) {
  return logAdminEvent("ask_session_started", userId, { sessionId });
}

export function logAskMessageSent(
  userId: string,
  sessionId: string,
  tokensUsed: number,
  hasAttachment: boolean
) {
  return logAdminEvent("ask_message_sent", userId, {
    sessionId,
    tokensUsed,
    hasAttachment,
  });
}

export function logAskMessageReceived(
  userId: string,
  sessionId: string,
  tokenUsage: TokenUsage,
  provider?: string,
  model?: string
) {
  return logAdminEvent("ask_message_received", userId, {
    sessionId,
    ...tokenUsage,
    provider,
    model,
  });
}

export function logDraftCreated(
  userId: string,
  draftId: string,
  mode: "doc" | "deck",
  title: string
) {
  return logAdminEvent("draft_created", userId, { draftId, mode, title });
}

export function logDraftChatMessage(
  userId: string,
  draftId: string,
  role: "user" | "assistant",
  tokenUsage?: TokenUsage,
  provider?: string,
  model?: string
) {
  return logAdminEvent("draft_chat_message", userId, {
    draftId,
    role,
    ...(tokenUsage ?? {}),
    provider,
    model,
  });
}

export function logDraftModified(
  userId: string,
  draftId: string,
  tokenUsage?: TokenUsage,
  provider?: string,
  model?: string
) {
  return logAdminEvent("draft_modified", userId, {
    draftId,
    ...(tokenUsage ?? {}),
    provider,
    model,
  });
}

export function logAnalysisStarted(
  userId: string,
  jobId: string,
  fileType: string,
  analysisType: string,
  rowCount: number
) {
  return logAdminEvent("analysis_started", userId, {
    jobId,
    fileType,
    analysisType,
    rowCount,
  });
}

export function logAnalysisCompleted(
  userId: string,
  jobId: string,
  executionTimeSeconds: number,
  status: string
) {
  return logAdminEvent("analysis_completed", userId, {
    jobId,
    executionTimeSeconds,
    status,
  });
}

export function logRfpProjectCreated(userId: string, projectId: string) {
  return logAdminEvent("rfp_project_created", userId, { projectId });
}

export function logRfpStepCompleted(
  userId: string,
  projectId: string,
  step: string,
  tokenUsage?: TokenUsage,
  provider?: string,
  model?: string
) {
  return logAdminEvent("rfp_step_completed", userId, {
    projectId,
    step,
    ...(tokenUsage ?? {}),
    provider,
    model,
  });
}

export function logRfqProjectCreated(userId: string, projectId: string) {
  return logAdminEvent("rfq_project_created", userId, { projectId });
}

export function logRfqStepCompleted(
  userId: string,
  projectId: string,
  step: string,
  tokenUsage?: TokenUsage,
  provider?: string,
  model?: string
) {
  return logAdminEvent("rfq_step_completed", userId, {
    projectId,
    step,
    ...(tokenUsage ?? {}),
    provider,
    model,
  });
}

export function logSupplierEvalCreated(userId: string, evaluationId: string) {
  return logAdminEvent("supplier_eval_created", userId, { evaluationId });
}

export function logUserActive(userId: string, page: string) {
  return logAdminEvent("user_active", userId, { page });
}

// ─── Read ────────────────────────────────────────────────────────────────────

/**
 * Subscribe to recent admin events (real-time).
 * Returns an unsubscribe function.
 */
export function subscribeToRecentEvents(
  callback: (events: AdminEvent[]) => void,
  count = 50
): Unsubscribe {
  const q = query(
    collection(db, EVENTS),
    orderBy("timestamp", "desc"),
    fbLimit(count)
  );
  return onSnapshot(q, (snap) => {
    const events = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as AdminEvent[];
    callback(events);
  });
}

/**
 * Fetch events for a time range (client-side filter).
 * Used by the admin dashboard for aggregated queries.
 */
export async function getEventsInRange(
  startDate: Date,
  endDate: Date,
  eventType?: AdminEventType
): Promise<AdminEvent[]> {
  const { getDocs } = await import("firebase/firestore");
  let q;
  if (eventType) {
    q = query(
      collection(db, EVENTS),
      where("type", "==", eventType),
      where("timestamp", ">=", Timestamp.fromDate(startDate)),
      where("timestamp", "<=", Timestamp.fromDate(endDate)),
      orderBy("timestamp", "desc")
    );
  } else {
    q = query(
      collection(db, EVENTS),
      where("timestamp", ">=", Timestamp.fromDate(startDate)),
      where("timestamp", "<=", Timestamp.fromDate(endDate)),
      orderBy("timestamp", "desc")
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as AdminEvent[];
}
